'use client'

import { useLocale } from 'next-intl'
import { useRouter, usePathname } from '@/i18n/navigation'
import { Button } from '@/components/ui/button'
import { localesMeta } from '@/i18n/config'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Globe } from 'lucide-react'

function setLocaleCookie(code: string) {
  document.cookie = `NEXT_LOCALE=${code};path=/;max-age=${365 * 24 * 60 * 60};samesite=lax`
}

/**
 * Short label for the target locale shown in the toggle button.
 * - Latin-script locales → uppercase code (EN, FR, TR)
 * - Non-Latin locales → short native word (عربي for Arabic)
 */
const SHORT_LABELS: Record<string, string> = {
  ar: 'عربي',
}
function shortLabel(code: string): string {
  return SHORT_LABELS[code] ?? code.toUpperCase()
}

export function LocaleSwitcher() {
  const locale = useLocale()
  const router = useRouter()
  const pathname = usePathname()

  function switchTo(code: string) {
    setLocaleCookie(code)
    router.replace(pathname, { locale: code })
  }

  // Simple toggle for exactly 2 locales
  if (localesMeta.length === 2) {
    const other = localesMeta.find((l) => l.code !== locale) ?? localesMeta[0]!
    return (
      <Button
        variant="outline"
        size="icon"
        onClick={() => switchTo(other.code)}
        className="h-9 w-9 shrink-0 transition-all duration-200 font-semibold text-xs"
        aria-label={`Switch to ${other.nameEn}`}
      >
        {shortLabel(other.code)}
      </Button>
    )
  }

  // Dropdown for 3+ locales
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0"
          aria-label="Select language"
        >
          <Globe className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {localesMeta.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onClick={() => switchTo(l.code)}
            className={locale === l.code ? 'bg-accent' : ''}
          >
            <span className="mr-2">{l.nameNative}</span>
            <span className="text-xs text-muted-foreground">{l.nameEn}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
